'use client';

import type { Dispatch, SetStateAction } from 'react';
import { useRef, useEffect, useState } from 'react';

import Link from 'next/link';

import { useQuery } from '@tanstack/react-query';

import type { Entry } from '@hub/common/src/types/cit-public-api';

import { queryFunctions } from '@hub/common/src/query-functions';
import { getName, PLACEHOLDER_ENTRY } from '@hub/common/src/properties';
import { addTestThemeToHref, getEntryLink } from '@hub/common/src/navigationUtils';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';

import LinkIcon from './SVG/LinkIcon';
import MediaViewer from './MediaViewer';
import PropertiesView from './PropertiesView';
import { useCitThemeClient } from './CitThemeProvider';
import { ReferenceOverlay, type ReferenceType } from './ReferenceOverlay';
import { CitOrigin } from '@hub/common/src/types/cit-public-api';

interface EntryDetailLayoutProps {
  entry: Entry | null;
  enableGallery?: boolean;
  accountId: string;
  setButtonVisibility?: Dispatch<SetStateAction<'visible' | 'hidden' | 'locked'>>;
  buttonVisibility?: 'visible' | 'hidden' | 'locked';
  closeButton?: React.ReactNode;
  isOverlay?: boolean;
}

export default function EntryDetailLayout({
  enableGallery = true,
  entry,
  accountId,
  setButtonVisibility,
  buttonVisibility,
  closeButton,
  isOverlay = false
}: EntryDetailLayoutProps) {
  const [selectedReference, setSelectedReference] = useState<ReferenceType | undefined>();

  const [prevButtonVisibility, setPrevButtonVisibility] = useState<'visible' | 'hidden' | 'locked'>(
    buttonVisibility || 'hidden'
  );

  const entryToDisplay = entry || PLACEHOLDER_ENTRY;

  const { accountTheme } = useCitThemeClient();

  const citOrigin = useCitOriginClient();

  // tracks full page scroll on mobile
  const scrollRef = useRef<HTMLDivElement>(null);

  // tracks properties column scroll on desktop
  const propertiesScrollRef = useRef<HTMLDivElement>(null);

  // will recieve prefetched account details query from parent page
  const { data: accountDetails } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  useEffect(() => {
    if (selectedReference) {
      setPrevButtonVisibility(buttonVisibility ?? 'hidden');
      setButtonVisibility?.('locked');
    } else {
      setButtonVisibility?.(prevButtonVisibility);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReference, setButtonVisibility, setPrevButtonVisibility]);

  useEffect(() => {
    let lastScrollTop = 0;
    let buttonsShouldBeVisible = false;

    const handleScroll = () => {
      if (!scrollRef.current || !setButtonVisibility) {
        return;
      }

      const element = scrollRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Check if we're at the bottom (with a small tolerance for rounding errors)
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 1;

      const isScrollingUp = scrollTop < lastScrollTop;
      const isScrollingDown = scrollTop > lastScrollTop;

      // Show buttons when scrolling up or at the bottom
      if (isScrollingUp || isAtBottom) {
        buttonsShouldBeVisible = true;
      }

      // Hide buttons when scrolling down (unless at bottom)
      if (isScrollingDown && !isAtBottom) {
        buttonsShouldBeVisible = false;
      }

      setButtonVisibility(buttonsShouldBeVisible ? 'visible' : 'hidden');

      // Update lastScrollTop for next comparison
      lastScrollTop = scrollTop;
    };
    const propertiesScroll = propertiesScrollRef.current;
    const pageScroll = scrollRef.current;

    if (pageScroll && propertiesScroll && setButtonVisibility) {
      pageScroll.addEventListener('scroll', handleScroll);

      // Set initial opacity with longer delay to ensure layout is complete
      const initialOpacityTimeout = window.setTimeout(handleScroll, 400);

      // Also use ResizeObserver to handle content changes
      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(pageScroll);

      return () => {
        window.clearTimeout(initialOpacityTimeout);
        pageScroll.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();

        // TODO: possibly will need to apply IOS scroll fix to this as well
        // reset scroll position to top for full page on mobile and properties column on desktop on unmount
        window.setTimeout(() => {
          pageScroll.scrollTo({ top: 0, behavior: 'instant' });
          propertiesScroll.scrollTo({ top: 0, behavior: 'instant' });
        }, 600);
      };
    }
  }, [setButtonVisibility, scrollRef]);

  // Prevent scroll on scrollable div when content is too short
  useEffect(() => {
    const div = scrollRef.current;
    if (!div) {
      return;
    }

    const handleTouchMove = (e: TouchEvent) => {
      const container = e.target as HTMLElement;

      // if the touch is on the properties container, don't prevent scrolling, that breaks tablet views scrolling properties column
      if (container.closest('.entry-detail-properties-container')) {
        return;
      }

      // Only prevent if the div itself can't scroll
      if (div.scrollHeight <= div.clientHeight) {
        e.preventDefault();
      }
    };

    div.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      div.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const thumbnails = entryToDisplay.media;
  const hasMedia = thumbnails && thumbnails.length > 0;

  const bgColor = `#${accountTheme.backgroundColor(entryToDisplay).replace('#', '')}`;

  const bodyFontColor = accountTheme.bodyFontColor(entryToDisplay);
  const titleFontColor = accountTheme.titleFontColor(entryToDisplay);

  const bodyFont = accountTheme.bodyFont();
  const titleFont = accountTheme.titleFont();

  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  return (
    <div className='entry-detail-and-profile-overlay-container relative h-full w-full flex flex-col'>
      <div
        className={`entry-detail-scroll-container flex flex-col h-full w-full pl-3 gap-6 overflow-y-auto lg:overflow-y-hidden overscroll-contain`}
        style={{
          backgroundColor: bgColor,
          color: bodyFontColor,
          fontFamily: bodyFont
        }}
        // don't track scroll position if profile overlay is open
        ref={buttonVisibility !== 'locked' && !selectedReference ? scrollRef : undefined}
      >
        {/* Title Section */}
        <div className='entry-detail-title-section flex flex-col w-full px-3 pt-4 gap-1'>
          <div className='flex justify-between items-center overflow-hidden'>
            {/* when entry is an overlay make title clickable to open the entry in a full page view */}
            {/* purposefully using <a> instead of <Link> to prevent interceptor route from being triggered */}
            {isOverlay ? (
              <a
                href={addTestThemeToHref(
                  getEntryLink(entryToDisplay.slug || entryToDisplay.id, citOrigin, accountId),
                  accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
                )}
              >
                <div className='flex items-center gap-2'>
                  <h1
                    className='text-3xl font-bold hover:underline cursor-pointer line-clamp-1 break-all'
                    style={{
                      fontFamily: titleFont,
                      color: titleFontColor,
                      lineHeight,
                      letterSpacing
                    }}
                  >
                    {getName(entryToDisplay.properties).replace(/"/g, '')}
                  </h1>
                  <LinkIcon width={24} height={24} />
                </div>
              </a>
            ) : (
              <h1
                className='text-3xl font-bold line-clamp-1 break-all'
                style={{
                  fontFamily: titleFont,
                  color: titleFontColor,
                  lineHeight,
                  letterSpacing
                }}
              >
                {getName(entryToDisplay.properties).replace(/"/g, '')}
              </h1>
            )}
            {/* the modal's or the reference overlay's close button, top right */}
            {closeButton}
          </div>

          <div style={{ color: bodyFontColor }} className='flex flex-row gap-2 items-center'>
            <h2 style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
              {entryToDisplay.classification}
            </h2>
            {/* disable link in iframe view */}
            {citOrigin !== CitOrigin.IFRAME ? (
              <>
                <span style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>-</span>
                <Link
                  href={addTestThemeToHref(
                    `/${citOrigin !== CitOrigin.HUBPRO ? accountDetails?.slug || accountId : ''}`,
                    accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
                  )}
                  prefetch={false}
                >
                  <h3
                    style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}
                    className='truncate underline'
                  >
                    {accountTheme.accountName()}
                  </h3>
                </Link>
              </>
            ) : null}
          </div>
        </div>

        {/* Media & Properties Block */}
        <div className='entry-detail-media-and-properties-container flex flex-col lg:flex-row w-full lg:h-full p-3 gap-6 lg:overflow-y-hidden'>
          {/* Media Column */}
          {/* if there is no media, we don't need to show the media column */}
          {hasMedia ? (
            <div className='entry-detail-media-container flex flex-col w-full lg:w-7/12 lg:h-full lg:overflow-y-hidden'>
              <MediaViewer
                thumbnails={thumbnails}
                // a reference overlay open over the entry holds the one lightgallery
                enableGallery={enableGallery && !selectedReference}
                onScreen={enableGallery}
                accountId={accountId}
                detailId={entryToDisplay.slug ?? entryToDisplay.id}
              />
            </div>
          ) : null}

          {/* Properties Column */}
          <div
            className={`entry-detail-properties-container flex flex-col w-full lg:w-5/12 lg:h-full lg:overflow-y-auto gap-4 pb-14 lg:pb-0 overscroll-contain ${
              hasMedia ? '' : 'lg:w-full'
            }`}
            ref={propertiesScrollRef}
          >
            <PropertiesView
              properties={entryToDisplay.properties}
              accountId={accountId}
              setSelectedReference={!isOverlay ? setSelectedReference : undefined}
              // should not be needed since you can't relate an entry to itself, but used just in case
              // references the current entry in the layout not the reference overlay, used so it won't link to itself
              currentReference={
                entryToDisplay
                  ? {
                      referenceLink: entryToDisplay.slug || entryToDisplay.id,
                      referenceType: 'entry'
                    }
                  : undefined
              }
            />
          </div>
        </div>
      </div>
      <ReferenceOverlay
        selectedReference={selectedReference}
        setSelectedReference={setSelectedReference}
        accountId={accountId}
      />
    </div>
  );
}
